// tokens.js — the single source of the palette → CSS-custom-property contract.
//
// paletteToVars(p) flattens a palette (see registry.js) into the `--m-*`
// variables the app reads. Token names match the design handoff's palette-mock
// (`--m-bg`, `--m-panel`, `--m-fg`, `--m-accent`, …) and are extended with the
// wire tokens and a few app-specific semantic aliases (`--m-overlay`,
// `--m-danger`, `--m-online`) discovered during surface migration.
//
// Adding a new themeable token: add it here (deriving from palette fields) and
// give it a matching default in tokens.css. Nothing else needs to change.

// Derive an rgba overlay-scrim color appropriate to the palette's tone. Dark
// palettes darken (black scrim); light palettes still darken but softer, so
// modals/backdrops read on cream without going muddy.
function overlayFor(p) {
  return p.isLight ? 'rgba(40,30,20,0.35)' : 'rgba(0,0,0,0.6)'
}

// A sensible danger/destructive color per tone (reset/clear/delete affordances).
function dangerFor(p) {
  return p.isLight ? '#b0442e' : '#d06a5a'
}

// Warning/attention amber per tone (dirty state, stale drafts, pending audio).
function warnFor(p) {
  return p.isLight ? '#9c6b1f' : '#e6b45a'
}

export function paletteToVars(p) {
  return {
    // surfaces
    '--m-bg': p.bg,
    '--m-bg-graph': p.bgGraph,
    '--m-panel': p.panel,
    '--m-panel-hi': p.panelHi,
    '--m-line': p.line,
    '--m-line-hi': p.lineHi,
    '--m-node-bg': p.nodeBg,
    '--m-node-border': p.nodeBorder,
    '--m-grid-img': p.gridImg || 'none',
    // text
    '--m-fg': p.fg,
    '--m-fg-2': p.fg2,
    '--m-fg-3': p.fg3,
    // accent
    '--m-accent': p.accent,
    '--m-accent-soft': p.accentSoft,
    // Ink for text/glyphs sitting ON an accent fill (primary buttons). The
    // Studio pair declares dark mint-ink; legacy palettes default to the
    // white-ish ink their accent buttons have always drawn.
    '--m-accent-ink': p.accentInk || '#ffffff',
    // The 3D stage/viewport ground. The Studio pair pins it dark in BOTH
    // themes (gass .stu-stagecard precedent); legacy palettes fall back to
    // their graph background — exactly what the Canvas read before this
    // token existed, so their look is unchanged.
    '--m-stage': p.stage || p.bgGraph || '#101114',
    // typed wires
    '--m-wire-anim': p.wires.anim,
    '--m-wire-audio': p.wires.audio,
    '--m-wire-event': p.wires.event,
    '--m-wire-state': p.wires.state,
    '--m-wire-output': p.wires.output,
    '--m-wire-neutral': p.wireNeutral || 'rgba(255,255,255,0.35)',
    // status + semantic aliases
    '--m-online': p.statusOnline || '#7fcb6a',
    '--m-overlay': overlayFor(p),
    '--m-danger': p.danger || dangerFor(p),
    '--m-warn': p.statusWarn || warnFor(p),
    // chrome metrics + typography (Studio contract extension). Registry
    // entries all declare these explicitly; the fallbacks only cover ad-hoc
    // palette objects (tests, TOKEN_NAMES stub) and match LEGACY_CHROME.
    '--m-font': p.font || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
    // Monospace counterpart to --m-font: code/data readouts (ids, hashes,
    // numeric xyz fields, console output, keyboard-shortcut key labels).
    '--m-mono': p.mono || "ui-monospace, 'Cascadia Mono', Menlo, Consolas, monospace",
    '--m-radius': p.radius || '6px',
    '--m-radius-sm': p.radiusSm || '4px',
    '--m-shadow-card': p.shadowCard || '0 8px 24px rgba(0,0,0,0.5)',
    '--m-head-size': p.headSize || '10.5px',
    '--m-head-ls': p.headLs || '0.12em',
    '--m-ease': p.ease || '.12s',
  }
}

// The ordered list of every token name this contract owns — used by tests and
// by applyPalette to guarantee a full, consistent set is always written.
export const TOKEN_NAMES = Object.keys(
  paletteToVars({
    bg: '', bgGraph: '', panel: '', panelHi: '', line: '', lineHi: '',
    nodeBg: '', nodeBorder: '', gridImg: '', fg: '', fg2: '', fg3: '',
    accent: '', accentSoft: '', wires: {}, isLight: false,
  }),
)
